import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Box,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import './Admin.css';
import db from '../firebase';
import { ref, onValue, remove, update, push, set, getDatabase } from 'firebase/database';
import emailjs from '@emailjs/browser';
import VacationMode from './VacationMode';
import BookingToggle from './BookingToggle';

function Admin() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [walkIns, setWalkIns] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
    type: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [activeTab, setActiveTab] = useState(0);
  const [addWalkInDialog, setAddWalkInDialog] = useState(false);
  const [walkInForm, setWalkInForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    notes: ''
  });
  const [notifyDialog, setNotifyDialog] = useState(false);
  const [affectedAppointments, setAffectedAppointments] = useState([]);

  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('adminAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    try {
      const appointmentsRef = ref(db, 'appointments');
      
      const unsubscribe = onValue(appointmentsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const appointmentsList = Object.entries(data).map(([key, value]) => ({
            ...value,
            firebaseKey: key
          }));
          const sortedAppointments = appointmentsList.sort((a, b) => {
            const statusOrder = {
              pending: 0,
              confirmed: 1,
              cancelled: 2
            };
            return statusOrder[a.status] - statusOrder[b.status];
          });
          setAppointments(sortedAppointments);
        } else {
          setAppointments([]);
        }
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up appointments listener:', error);
      setSnackbar({
        open: true,
        message: 'Error loading appointments',
        severity: 'error'
      });
    }
  }, []);

  useEffect(() => {
    try {
      const walkInsRef = ref(db, 'walkIns');
      const walkInRequestsRef = ref(db, 'walkins');
      
      // Fetch regular walk-ins
      const walkInsUnsubscribe = onValue(walkInsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const walkInsList = Object.entries(data).map(([key, value]) => ({
            ...value,
            firebaseKey: key
          })).sort((a, b) => {
            const statusOrder = {
              waiting: 0,
              'in-progress': 1,
              completed: 2
            };
            const statusDiff = statusOrder[a.status || 'waiting'] - statusOrder[b.status || 'waiting'];
            
            if (statusDiff === 0) {
              return new Date(a.arrivalTime) - new Date(b.arrivalTime);
            }
            
            return statusDiff;
          });
          setWalkIns(prevWalkIns => {
            // Filter out walk-in requests that are already in the list
            const walkInIds = walkInsList.map(w => w.id);
            const filteredPrevWalkIns = prevWalkIns.filter(w => 
              w.source === 'request' && !walkInIds.includes(w.id)
            );
            return [...walkInsList, ...filteredPrevWalkIns];
          });
        } else {
          setWalkIns(prevWalkIns => prevWalkIns.filter(w => w.source === 'request'));
        }
      });

      // Fetch walk-in requests from the new component
      const requestsUnsubscribe = onValue(walkInRequestsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const requestsList = Object.entries(data).map(([key, value]) => ({
            ...value,
            firebaseKey: key,
            source: 'request',
            status: value.status || 'waiting'
          })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          
          setWalkIns(prevWalkIns => {
            // Filter out existing walk-ins that are not from requests
            const existingWalkIns = prevWalkIns.filter(w => w.source !== 'request');
            return [...existingWalkIns, ...requestsList];
          });
        }
      });

      return () => {
        walkInsUnsubscribe();
        requestsUnsubscribe();
      };
    } catch (error) {
      console.error('Error setting up walk-ins listener:', error);
      setSnackbar({
        open: true,
        message: 'Error loading walk-ins',
        severity: 'error'
      });
    }
  }, []);

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending' },
      confirmed: { color: 'success', label: 'Confirmed' },
      cancelled: { color: 'error', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        className="status-chip"
      />
    );
  };

  const handleDeleteAppointment = (appointmentId) => {
    const appointment = appointments.find(app => app.id === appointmentId);
    setConfirmDialog({
      open: true,
      title: 'Delete Appointment',
      message: `Are you sure you want to delete the appointment for ${appointment.name}?`,
      action: async () => {
        try {
          const appointmentRef = ref(db, `appointments/${appointment.firebaseKey}`);
          await remove(appointmentRef);
          setSnackbar({
            open: true,
            message: 'Appointment deleted successfully',
            severity: 'success'
          });
          setConfirmDialog({ ...confirmDialog, open: false });
        } catch (error) {
          console.error('Error deleting appointment:', error);
          setSnackbar({
            open: true,
            message: 'Error deleting appointment',
            severity: 'error'
          });
        }
      },
      type: 'delete'
    });
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenDialog(true);
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const appointment = appointments.find(app => app.id === appointmentId);
      const appointmentRef = ref(db, `appointments/${appointment.firebaseKey}`);
      
      await update(appointmentRef, { status: newStatus });

      const emailParams = {
        to_name: appointment.name,
        to_email: appointment.email,
        appointment_date: appointment.date,
        appointment_time: appointment.time,
        service: appointment.service,
        status: newStatus,
        message: newStatus === 'confirmed' 
          ? "Your appointment has been confirmed! We look forward to seeing you."
          : "Your appointment has been cancelled. Please contact us if you have any questions."
      };

      try {
        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID_STATUS,
          emailParams,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );
        console.log('Status update email sent successfully');
      } catch (emailError) {
        console.error('Error sending status update email:', emailError);
      }

      setSnackbar({
        open: true,
        message: `Appointment ${newStatus} successfully and notification email sent`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating appointment status:', error);
      setSnackbar({
        open: true,
        message: 'Error updating appointment status',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleAddWalkIn = async () => {
    try {
      const db = getDatabase();
      const walkInsRef = ref(db, 'walkIns');
      
      const newWalkIn = {
        id: Date.now(),
        ...walkInForm,
        type: 'walk-in',
        status: 'waiting',
        arrivalTime: new Date().toISOString(),
        queueNumber: walkIns.filter(w => w.source !== 'request').length + 1
      };

      const newWalkInRef = push(walkInsRef);
      await set(newWalkInRef, newWalkIn);
      
      const todaysAppointments = appointments.filter(app => {
        if (app.status !== 'confirmed') return false;
        
        const appDate = new Date(app.date);
        const today = new Date();
        return appDate.getDate() === today.getDate() && 
               appDate.getMonth() === today.getMonth() && 
               appDate.getFullYear() === today.getFullYear();
      });
      
      if (todaysAppointments.length > 0) {
        setAffectedAppointments(todaysAppointments);
        setNotifyDialog(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Walk-in customer added successfully',
          severity: 'success'
        });
      }
      
      setWalkInForm({
        name: '',
        phone: '',
        email: '',
        service: '',
        notes: ''
      });
      setAddWalkInDialog(false);
    } catch (error) {
      console.error('Error adding walk-in:', error);
      setSnackbar({
        open: true,
        message: 'Error adding walk-in customer',
        severity: 'error'
      });
    }
  };

  const handleNotifyCustomers = async () => {
    try {
      for (const appointment of affectedAppointments) {
        const emailParams = {
          to_name: appointment.name,
          to_email: appointment.email,
          appointment_date: appointment.date,
          appointment_time: appointment.time,
          service: appointment.service,
          message: "We wanted to inform you that we have received a walk-in customer that may affect your appointment time. There might be a slight delay in your service. We apologize for any inconvenience and appreciate your understanding."
        };

        try {
          await emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID_STATUS,
            emailParams,
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
          );
        } catch (emailError) {
          console.error('Error sending notification email:', emailError);
        }
      }
      
      setSnackbar({
        open: true,
        message: `Walk-in added and ${affectedAppointments.length} customer(s) notified`,
        severity: 'success'
      });
      setNotifyDialog(false);
    } catch (error) {
      console.error('Error notifying customers:', error);
      setSnackbar({
        open: true,
        message: 'Error notifying customers',
        severity: 'error'
      });
    }
  };

  const handleWalkInStatusChange = async (walkInId, newStatus) => {
    try {
      const walkIn = walkIns.find(w => w.id === walkInId);
      const walkInRef = ref(db, `walkIns/${walkIn.firebaseKey}`);
      
      await update(walkInRef, { 
        status: newStatus,
        statusUpdatedAt: new Date().toISOString()
      });

      setSnackbar({
        open: true,
        message: `Walk-in status updated to ${newStatus}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating walk-in status:', error);
      setSnackbar({
        open: true,
        message: 'Error updating walk-in status',
        severity: 'error'
      });
    }
  };

  const handleDeleteWalkIn = (walkInId) => {
    const walkIn = walkIns.find(w => w.id === walkInId);
    setConfirmDialog({
      open: true,
      title: 'Delete Walk-in',
      message: `Are you sure you want to remove ${walkIn.name} from the walk-in queue?`,
      action: async () => {
        try {
          const walkInRef = ref(db, `walkIns/${walkIn.firebaseKey}`);
          await remove(walkInRef);
          setSnackbar({
            open: true,
            message: 'Walk-in removed successfully',
            severity: 'success'
          });
          setConfirmDialog({ ...confirmDialog, open: false });
        } catch (error) {
          console.error('Error deleting walk-in:', error);
          setSnackbar({
            open: true,
            message: 'Error removing walk-in',
            severity: 'error'
          });
        }
      },
      type: 'delete'
    });
  };

  const handleWalkInFormChange = (e) => {
    const { name, value } = e.target;
    setWalkInForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getWalkInStatusChip = (status) => {
    const statusConfig = {
      waiting: { color: 'warning', label: 'Waiting' },
      'in-progress': { color: 'info', label: 'In Progress' },
      completed: { color: 'success', label: 'Completed' }
    };

    const config = statusConfig[status] || statusConfig.waiting;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        className="status-chip"
      />
    );
  };

  const formatWaitTime = (arrivalTime) => {
    const now = new Date();
    const arrival = new Date(arrivalTime);
    const diffMs = now - arrival;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''}`;
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours} hr${hours !== 1 ? 's' : ''} ${mins} min${mins !== 1 ? 's' : ''}`;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const services = [
    'Haircut',
    'Beard Trim',
    'Hot Towel Shave',
    'Hair Coloring',
    'Kids Haircut',
    'Complete Package'
  ];

  const handleApproveWalkInRequest = async (walkInId) => {
    try {
      const walkIn = walkIns.find(w => w.id === walkInId);
      if (!walkIn || walkIn.source !== 'request') return;
      
      // Remove from requests
      const requestRef = ref(db, `walkins/${walkIn.firebaseKey}`);
      await remove(requestRef);
      
      // Add to regular walk-ins queue
      const walkInsRef = ref(db, 'walkIns');
      const newWalkIn = {
        id: walkIn.id,
        name: walkIn.name,
        phone: walkIn.phone,
        email: walkIn.email || '',
        service: walkIn.service,
        notes: walkIn.notes || '',
        type: 'walk-in',
        status: 'waiting',
        arrivalTime: new Date().toISOString(),
        queueNumber: walkIns.filter(w => w.source !== 'request').length + 1
      };
      
      const newWalkInRef = push(walkInsRef);
      await set(newWalkInRef, newWalkIn);
      
      setSnackbar({
        open: true,
        message: 'Walk-in request approved and added to queue',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error approving walk-in request:', error);
      setSnackbar({
        open: true,
        message: 'Error approving walk-in request',
        severity: 'error'
      });
    }
  };

  return (
    <Container maxWidth="lg" className="admin-container">
      <Paper elevation={3} className="admin-paper" sx={{ p: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Shop Management
        </Typography>
        
        <Box sx={{ mb: 3, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <BookingToggle />
          <VacationMode />
        </Box>
        
        {/* Tabs for Appointments and Walk-ins */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="management tabs">
            <Tab label="Appointments" />
            <Tab label="Walk-in Queue" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <>
            <Typography variant="h5" component="h2" gutterBottom>
              Scheduled Appointments
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appointment) => (
                    <TableRow key={appointment.id}>
                      <TableCell>{appointment.name}</TableCell>
                      <TableCell>{appointment.service}</TableCell>
                      <TableCell>{appointment.date}</TableCell>
                      <TableCell>{appointment.time}</TableCell>
                      <TableCell>
                        {getStatusChip(appointment.status)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Confirm Appointment">
                            <IconButton
                              size="small"
                              color={appointment.status === 'confirmed' ? 'success' : 'default'}
                              disabled={appointment.status !== 'pending'}
                              onClick={() => handleStatusChange(appointment.id, 'confirmed')}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel Appointment">
                            <IconButton
                              size="small"
                              color={appointment.status === 'cancelled' ? 'error' : 'default'}
                              disabled={appointment.status !== 'pending'}
                              onClick={() => handleStatusChange(appointment.id, 'cancelled')}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewDetails(appointment)}
                          >
                            View
                          </Button>
                          <Tooltip title="Delete Appointment">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAppointment(appointment.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {appointments.length === 0 && (
              <Typography variant="body1" align="center" sx={{ my: 3 }}>
                No appointments found
              </Typography>
            )}
          </>
        )}

        {/* Walk-ins Tab */}
        {activeTab === 1 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" component="h2">
                Walk-in Queue
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setAddWalkInDialog(true)}
              >
                Add Walk-in
              </Button>
            </Box>
            
            {/* Walk-in Requests Section */}
            {walkIns.some(w => w.source === 'request') && (
              <>
                <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
                  Pending Walk-in Requests
                </Typography>
                <TableContainer sx={{ mb: 4 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Requested At</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {walkIns.filter(w => w.source === 'request').map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>{request.name}</TableCell>
                          <TableCell>{request.phone}</TableCell>
                          <TableCell>{request.service}</TableCell>
                          <TableCell>{new Date(request.createdAt).toLocaleString()}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => handleApproveWalkInRequest(request.id)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setSelectedAppointment(request);
                                  setOpenDialog(true);
                                }}
                              >
                                View
                              </Button>
                              <Tooltip title="Delete Request">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteWalkIn(request.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {/* Regular Walk-ins Section */}
            <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
              Current Walk-ins
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Queue #</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Wait Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {walkIns.filter(w => w.source !== 'request').map((walkIn, index) => (
                    <TableRow key={walkIn.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{walkIn.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{walkIn.phone}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{walkIn.service}</TableCell>
                      <TableCell>{formatWaitTime(walkIn.arrivalTime)}</TableCell>
                      <TableCell>{getWalkInStatusChip(walkIn.status || 'waiting')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {walkIn.status !== 'in-progress' && walkIn.status !== 'completed' && (
                            <Tooltip title="Start Service">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleWalkInStatusChange(walkIn.id, 'in-progress')}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {walkIn.status === 'in-progress' && (
                            <Tooltip title="Complete Service">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleWalkInStatusChange(walkIn.id, 'completed')}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedAppointment(walkIn);
                              setOpenDialog(true);
                            }}
                          >
                            View
                          </Button>
                          <Tooltip title="Remove from Queue">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteWalkIn(walkIn.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {walkIns.filter(w => w.source !== 'request').length === 0 && (
              <Typography variant="body1" align="center" sx={{ my: 3 }}>
                No walk-in customers in queue
              </Typography>
            )}
          </>
        )}

        {/* Details Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Customer Details</DialogTitle>
          <DialogContent>
            {selectedAppointment && (
              <Box sx={{ pt: 2 }}>
                <Typography><strong>Name:</strong> {selectedAppointment.name}</Typography>
                <Typography><strong>Email:</strong> {selectedAppointment.email}</Typography>
                <Typography><strong>Phone:</strong> {selectedAppointment.phone}</Typography>
                <Typography><strong>Service:</strong> {selectedAppointment.service}</Typography>
                {selectedAppointment.date && (
                  <Typography><strong>Date:</strong> {selectedAppointment.date}</Typography>
                )}
                {selectedAppointment.time && (
                  <Typography><strong>Time:</strong> {selectedAppointment.time}</Typography>
                )}
                {selectedAppointment.status && (
                  <Typography><strong>Status:</strong> {
                    selectedAppointment.type === 'walk-in' 
                      ? getWalkInStatusChip(selectedAppointment.status)
                      : getStatusChip(selectedAppointment.status)
                  }</Typography>
                )}
                {selectedAppointment.arrivalTime && (
                  <Typography><strong>Arrival Time:</strong> {new Date(selectedAppointment.arrivalTime).toLocaleString()}</Typography>
                )}
                {selectedAppointment.notes && (
                  <Typography><strong>Notes:</strong> {selectedAppointment.notes}</Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Add Walk-in Dialog */}
        <Dialog open={addWalkInDialog} onClose={() => setAddWalkInDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Walk-in Customer</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={walkInForm.name}
                  onChange={handleWalkInFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={walkInForm.phone}
                  onChange={handleWalkInFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={walkInForm.email}
                  onChange={handleWalkInFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Service</InputLabel>
                  <Select
                    name="service"
                    value={walkInForm.service}
                    onChange={handleWalkInFormChange}
                    label="Service"
                  >
                    {services.map((service) => (
                      <MenuItem key={service} value={service}>
                        {service}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  multiline
                  rows={3}
                  value={walkInForm.notes}
                  onChange={handleWalkInFormChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddWalkInDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddWalkIn}
              variant="contained"
              disabled={!walkInForm.name || !walkInForm.phone || !walkInForm.service}
            >
              Add to Queue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notify Customers Dialog */}
        <Dialog open={notifyDialog} onClose={() => setNotifyDialog(false)}>
          <DialogTitle>Notify Affected Customers</DialogTitle>
          <DialogContent>
            <DialogContentText>
              There are {affectedAppointments.length} scheduled appointment(s) for today that may be affected by this walk-in customer. Would you like to notify these customers about a potential delay?
            </DialogContentText>
            {affectedAppointments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Affected appointments:</Typography>
                <ul>
                  {affectedAppointments.map(app => (
                    <li key={app.id}>
                      {app.name} - {app.time} - {app.service}
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotifyDialog(false)}>Skip</Button>
            <Button onClick={handleNotifyCustomers} variant="contained" color="primary">
              Notify Customers
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
        >
          <DialogTitle sx={{ color: confirmDialog.type === 'delete' ? 'error.main' : 'inherit' }}>
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmDialog.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
            <Button 
              onClick={confirmDialog.action}
              color={confirmDialog.type === 'delete' ? 'error' : 'primary'}
              variant="contained"
              autoFocus
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}

export default Admin;
